/**
 * The replies a sender offers recipients when confirmation is requested.
 *
 * Mirrors `responseOptions.ts` in coms, which is the source of truth: the
 * backend re-validates whatever is posted and the Twilio inbound webhook
 * matches inbound texts against these same keys and labels. Keep the preset
 * labels identical to the ones there, since the label is what lands in the
 * delivery ledger and shows up on the report.
 */

export type ResponseOption = {
  /** What an SMS recipient texts back. */
  key: string;
  label: string;
  /** Whether choosing this counts as confirming receipt. */
  acknowledges: boolean;
};

export const RECEIPT_ONLY_OPTIONS: ResponseOption[] = [
  { key: '1', label: 'Acknowledged', acknowledges: true },
];

export const TRIAGE_OPTIONS: ResponseOption[] = [
  { key: '1', label: 'Acknowledged', acknowledges: true },
  { key: '2', label: 'Responding', acknowledges: true },
  { key: '3', label: 'Unavailable', acknowledges: true },
];

export type ResponsePresetKey = 'receipt' | 'triage';

export const RESPONSE_PRESETS: Record<ResponsePresetKey, ResponseOption[]> = {
  receipt: RECEIPT_ONLY_OPTIONS,
  triage: TRIAGE_OPTIONS,
};

export const RESPONSE_PRESET_CHOICES: {
  label: string;
  value: ResponsePresetKey;
}[] = [
  { label: 'Confirm receipt only', value: 'receipt' },
  { label: 'Acknowledged / Responding / Unavailable', value: 'triage' },
];

/**
 * The exact line appended to an outbound SMS, so the compose form can show the
 * sender what recipients will read rather than describing it in the abstract.
 */
export function describeSmsResponseOptions(options: ResponseOption[]): string {
  if (options.length === 1) {
    return `Reply ${options[0].key} to confirm receipt.`;
  }
  const choices = options.map((option) => `${option.key} for ${option.label}`);
  const last = choices.pop();
  return `Reply ${choices.join(', ')} or ${last}.`;
}

/** Which preset a stored option set corresponds to, for re-opening a draft. */
export function presetKeyOf(
  options: ResponseOption[] | null | undefined
): ResponsePresetKey {
  return options && options.length > 1 ? 'triage' : 'receipt';
}
